import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AuthApi } from '@/shared/api/auth'
import { ApiError } from '@/shared/api'
import { AlertCircle } from '@/ui/icons'
import { Button } from '@/ui/components/'
import { Input } from '@/ui/components/'
import { Logo } from '@/ui/components'
import { MEDIA_CONFIG } from '@/shared/utils'
import { PageMeta } from '@/components/PageMeta'

const publicAuthApi = new AuthApi(async () => null)

export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await publicAuthApi.requestPasswordReset(email.trim())
      setSubmitted(true)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || 'Something went wrong. Please try again.')
      } else {
        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <PageMeta
        title="Forgot password"
        description="Request a password reset link for your SitRepp.io account"
      />
      <div className="min-h-screen flex">
        <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative flex-col overflow-hidden">
          <img
            src={MEDIA_CONFIG.hero.image.src}
            alt="Overlanding convoy on a mountain trail"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="relative z-10 p-10">
            <Logo to="/" size="sm" className="h-8 brightness-0 invert" />
          </div>
          <div className="relative z-10 mt-auto p-10 pb-14">
            <p className="text-xs font-semibold text-white/60 mb-6">
              Coordinate · Plan · Explore
            </p>
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
              Back on the trail<br />in no time
            </h1>
            <p className="mt-4 text-base xl:text-lg text-white/75 max-w-sm leading-relaxed">
              We will email you a secure link to choose a new password.
            </p>
          </div>
        </div>

        <div
          className="w-full lg:w-1/2 xl:w-2/5 relative flex flex-col min-h-screen"
          style={{
            backgroundImage: `url(${MEDIA_CONFIG.hero.image.src})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm lg:hidden" />
          <div className="absolute inset-0 bg-background hidden lg:block" />

          <div className="relative z-10 flex flex-col min-h-screen px-8 py-10 sm:px-12">
            <div className="flex justify-center mb-8 lg:hidden">
              <Logo to="/" size="sm" className="h-10 brightness-0 invert" />
            </div>

            <div className="flex-1 flex flex-col justify-center w-full max-w-sm mx-auto">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-foreground lg:text-foreground text-white lg:text-current">
                  Forgot password?
                </h2>
                <p className="mt-1.5 text-sm text-muted-foreground lg:text-muted-foreground text-white/70 lg:text-current">
                  Enter the email you use for SitRepp. If an account exists, we will send reset instructions.
                </p>
              </div>

              {submitted ? (
                <div
                  role="status"
                  className="rounded-xl border border-border bg-card/80 backdrop-blur-sm px-4 py-4 text-sm text-foreground lg:bg-card lg:backdrop-blur-none"
                >
                  <p className="font-medium text-foreground">Check your email</p>
                  <p className="mt-2 text-muted-foreground">
                    If an account exists for that address, you will receive a link shortly. You can close this tab
                    or return to sign in.
                  </p>
                  <Button asChild className="mt-6 w-full h-11 rounded-xl font-semibold" variant="secondary">
                    <Link to="/login">Back to sign in</Link>
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div
                      role="alert"
                      className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm flex items-start gap-2"
                    >
                      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span className="flex-1">{error}</span>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <label
                      htmlFor="forgot-email"
                      className="text-sm font-medium text-foreground lg:text-foreground text-white lg:text-current"
                    >
                      Email address
                    </label>
                    <Input
                      id="forgot-email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="h-11 rounded-xl"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 rounded-xl font-semibold"
                    disabled={loading}
                  >
                    {loading ? 'Sending…' : 'Send reset link'}
                  </Button>
                </form>
              )}

              <p className="mt-8 text-center text-sm text-muted-foreground lg:text-muted-foreground text-white/60">
                Remember your password?{' '}
                <Link
                  to="/login"
                  className="font-semibold text-primary hover:text-primary/80 transition-colors lg:text-primary text-white hover:text-white/80"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
